import $ from 'jquery'
import ReactRailsUJS from 'react_ujs'

declare var gf: Object

// Cache parsed props to avoid repeated JSON.parse
const propsCache = new WeakMap()

function getProps(selectEl) {
  if (!propsCache.has(selectEl))
    propsCache.set(selectEl, JSON.parse(selectEl.dataset.reactProps))

  return propsCache.get(selectEl)
}

function setProps(selectEl, props) {
  propsCache.set(selectEl, props)
  selectEl.dataset.reactProps = JSON.stringify(props)
}

// Reuse placeholder objects
const LOADING_PLACEHOLDER = Object.freeze({
  label: I18n.t('shared.loading'),
  value: '',
  disabled: true,
  imageData: {fa_icon: 'fa-user'},
})

const DEFAULT_PLACEHOLDER = Object.freeze({
  label: I18n.t('shared.select_dot_dot_dot'),
  disabled: true,
  imageData: {fa_icon: 'fa-user'},
})

function setLoadingState(selectEl, label = I18n.t('shared.loading')) {
  const props = getProps(selectEl)

  if (props.defaultValue)
    props.options = Array.isArray(props.defaultValue) ? props.defaultValue : [props.defaultValue]
  else
    props.options = []

  props.placeholderData = label === LOADING_PLACEHOLDER.label
    ? LOADING_PLACEHOLDER
    : {...LOADING_PLACEHOLDER, label}

  setProps(selectEl, props)
  ReactRailsUJS.mountComponents(selectEl.parentElement)
}

// Don't break the existing role_id_parent_role_id paradigm, but start getting ready for the new
// role_id/person_id/individual_initiative combo version.
function syncronizeTransitionalHiddenInputs(roleIdParentRoleId, form) {
  let roleId = roleIdParentRoleId.split(',')[0]

  const individualInitiative = (roleId === '-1')

  if (individualInitiative)
    roleId = roleIdParentRoleId.split(',')[1]

  const hiddenRoleId = form.find('[name$="[role_id]"]')
  if (hiddenRoleId.length > 0)
    hiddenRoleId[0].value = roleId

  const hiddenIndividualInitiative = form.find('[name$="[individual_initiative]"]')
  if (hiddenIndividualInitiative.length > 0)
    hiddenIndividualInitiative[0].value = individualInitiative
}

function renderOptions(selectEl, options, placeholder, eventType) {
  const props = getProps(selectEl)
  props.options = options

  if (eventType === 'role-changed' && (!props.defaultValue || !options.includes(props.defaultValue)))
    props.defaultValue = options.length === 1 ? [options[0]] : null

  props.placeholderData = placeholder === DEFAULT_PLACEHOLDER.label
    ? DEFAULT_PLACEHOLDER
    : {...DEFAULT_PLACEHOLDER, label: placeholder}

  // Batch unmount/mount to reduce reflows
  ReactRailsUJS.unmountComponents(selectEl.parentElement)
  setProps(selectEl, props)
  ReactRailsUJS.mountComponents(selectEl.parentElement)
}

// Use AbortController per form to cancel stale requests
const abortControllers = new WeakMap()
const dataCache = new Map() // Cache parsed data instead of promises

async function loadPeople(event) {
  const $form = $(event.target).closest('form')
  const form = $form[0]

  // Guard: if no form found, bail out to avoid WeakMap issues
  if (!form)
    return

  // Cancel previous request for this form
  const prevController = abortControllers.get(form)
  if (prevController)
    prevController.abort()

  const controller = new AbortController()
  abortControllers.set(form, controller)

  const {roleIdParentRoleId, organizationId} = event.originalEvent.detail
  const url = `/organizations/${organizationId}/dropdown_options/people?role_id_parent_role_id=${roleIdParentRoleId}`

  if (roleIdParentRoleId)
    syncronizeTransitionalHiddenInputs(roleIdParentRoleId, $form)

  // Use more efficient selector - find once, cache the result
  const personSelect = $form.find('[name$="[person_id]"]').closest('[data-react-class*="Glassfrog.components.ui"]')[0]
  if (!personSelect)
    return

  setLoadingState(personSelect, I18n.t('shared.loading'))

  // Check if we have cached data for this URL
  if (dataCache.has(url)) {
    const cachedData = dataCache.get(url)
    renderOptions(personSelect, cachedData, I18n.t('shared.select_dot_dot_dot'), event.type)
    return
  }

  // Check if there's an in-flight request for this URL
  if (!gf.asyncLoadPeoplePromises)
    gf.asyncLoadPeoplePromises = {}

  let promise = gf.asyncLoadPeoplePromises[url]

  if (!promise) {
    promise = fetch(url, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok)
          throw new Error(I18n.t('shared.dropdown.error_loading_people'))

        const data = await response.json()
        dataCache.set(url, data) // Cache the parsed data
        return data
      })
      .catch((error) => {
        if (error.name === 'AbortError')
          return null
        renderOptions(personSelect, [], I18n.t('shared.dropdown.error_loading_people'), event.type)
        return null
      })
      .finally(() => {
        gf.asyncLoadPeoplePromises[url] = null
        if (abortControllers.get(form) === controller)
          abortControllers.delete(form)
      })

    gf.asyncLoadPeoplePromises[url] = promise
  }

  const data = await promise
  if (!data)
    return // Request was aborted

  renderOptions(personSelect, data, I18n.t('shared.select_dot_dot_dot'), event.type)
}

export function setupAsyncRolePersonDropdown() {
  // Use event delegation efficiently - already optimal
  $('body').on('role-changed', '.js-slick-role-select', loadPeople)
  $('body').on('role-init', '.js-slick-role-select', (event) => {
    // Use requestAnimationFrame instead of setTimeout for better performance
    requestAnimationFrame(() => loadPeople(event))
  })
}

export default {setupAsyncRolePersonDropdown}
